import React, { useState, useEffect } from 'react';
import { Alert } from 'react-native';
import { FormModal, Dropdown, TextField } from '../common';
import { useTheme } from '../../hooks/useTheme';
import { useAuth } from '../../hooks/useAuth';
import { API_URL } from '../../constants/config';
import { getToken } from '../../utils/storage';

interface SupportFormProps {
  visible: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const SUPPORT_CATEGORIES = [
  { value: 'technical', label: 'Technical Issue' },
  { value: 'billing', label: 'Billing & Payment' },
  { value: 'account', label: 'Account Issue' },
  { value: 'feature', label: 'Feature Request' },
  { value: 'other', label: 'Other' },
];

const SupportForm: React.FC<SupportFormProps> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  useTheme(); // colors reserved for future styling
  const { isAuthenticated } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState('technical');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible && !isAuthenticated) {
      setName('');
      setEmail('');
    }
  }, [visible, isAuthenticated]);

  const handleSubmit = async () => {
    if (!message.trim()) {
      Alert.alert('Error', 'Please describe your issue');
      return;
    }

    if (!subject.trim()) {
      Alert.alert('Error', 'Please enter a subject');
      return;
    }

    setLoading(true);
    try {
      const token = await getToken('auth_token');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URL}/feedback/`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name: name.trim() || undefined,
          email: email.trim() || undefined,
          subject: subject.trim(),
          message: message.trim(),
          category: 'support',
        }),
      });

      if (response.ok) {
        // Reset form
        setName('');
        setEmail('');
        setSubject('');
        setMessage('');
        setCategory('technical');

        // Close modal and show toast
        onClose();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        throw new Error('Failed to submit support request');
      }
    } catch {
      Alert.alert(
        'Error',
        'Failed to submit support request. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormModal
      visible={visible}
      onClose={onClose}
      title="Contact Support"
      onSubmit={handleSubmit}
      submitLabel="Submit Request"
      cancelLabel="Cancel"
      loading={loading}
    >
      {/* Category Dropdown */}
      <Dropdown
        label="Issue Type *"
        options={SUPPORT_CATEGORIES}
        value={category}
        onChange={setCategory}
      />

      {!isAuthenticated && (
        <TextField
          label="Name (Optional)"
          value={name}
          onChangeText={setName}
          placeholder="Enter your name"
        />
      )}

      {!isAuthenticated && (
        <TextField
          label="Email *"
          value={email}
          onChangeText={setEmail}
          placeholder="Enter your email"
          keyboardType="email-address"
          autoCapitalize="none"
        />
      )}

      <TextField
        label="Subject *"
        value={subject}
        onChangeText={setSubject}
        placeholder="Brief description of your issue"
      />

      <TextField
        label="Describe Your Issue *"
        value={message}
        onChangeText={setMessage}
        placeholder="Please provide as much detail as possible..."
        multiline
        numberOfLines={6}
      />
    </FormModal>
  );
};

export default SupportForm;
